import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { brand, neutral } from '@infinix/tokens';

/*
 * Nest brand marks (decision A-24): the mark (a lowercase "n" arch on the brand tile), the "nest" wordmark, the
 * horizontal lockup and the app icon family. Interim until a designer delivers the final mark; geometry here is the
 * single source, and packages/ui/assets holds the same marks as SVG files.
 */

/** The mark's glyph: a lowercase "n" drawn as one arch on a 48-unit grid. */
export const MARK_PATH = 'M17 34V22.5a7 7 0 0 1 14 0V34';

/**
 * The Nest mark. `tile` (default) is the white glyph on a brand tile; `tint` is a brand glyph on a light tile;
 * `plain` is the glyph alone (on photos or coloured surfaces). Minimum size 16px.
 */
export function NestMark({ size = 32, variant = 'tile', title }: { size?: number; variant?: 'tile' | 'tint' | 'plain'; title?: string }) {
  const bg = variant === 'tile' ? brand[600] : variant === 'tint' ? brand[100] : 'none';
  const fg = variant === 'tile' ? '#FFFFFF' : brand[600];
  // Thicker stroke at tiny sizes keeps the glyph legible as a favicon.
  const stroke = size <= 20 ? 6 : 5;
  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      sx={{ display: 'block', flexShrink: 0 }}
    >
      {variant !== 'plain' && <rect width="48" height="48" rx="13" fill={bg} />}
      <path d={MARK_PATH} fill="none" stroke={fg} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
    </Box>
  );
}

/** The wordmark "nest": Space Grotesk 600, tight tracking, lowercase. */
export function NestWordmark({ size = 20, color = neutral[900] }: { size?: number; color?: string }) {
  return (
    <Box component="span" sx={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontWeight: 600, fontSize: size, letterSpacing: '-0.03em', lineHeight: 1, color }}>
      nest
    </Box>
  );
}

/** Horizontal lockup: mark + wordmark. `onDark` for dark surfaces. Clear space = half the mark height. */
export function NestLogo({ size = 32, onDark = false }: { size?: number; onDark?: boolean }) {
  return (
    <Box role="img" aria-label="Nest" sx={{ display: 'inline-flex', alignItems: 'center', gap: `${Math.round(size * 0.34)}px` }}>
      <NestMark size={size} />
      <NestWordmark size={Math.round(size * 0.62)} color={onDark ? '#FFFFFF' : neutral[900]} />
    </Box>
  );
}

/* ---- app icon family ---------------------------------------------------------------------- */

/** White line glyphs on a 32-unit grid, one per app (drawn with the stroke set on the parent <g>). */
export const APP_GLYPHS: Record<string, ReactNode> = {
  payments: <><rect x="5" y="8" width="22" height="16" rx="3" /><path d="M5 13h22M9 19h5" /></>,
  org: <><path d="M7 26V9l9-4 9 4v17" /><path d="M12 26v-5h8v5M11 12h2M19 12h2M11 16h2M19 16h2" /></>,
  ops: <><path d="M16 4l10 4v7c0 6-4 10-10 13C10 25 6 21 6 15V8z" /><path d="M12 16l3 3 6-6" /></>,
  account: <><circle cx="16" cy="12" r="5" /><path d="M7 26c1.5-5 5-7 9-7s7.5 2 9 7" /></>,
  smartbill: <><path d="M9 4h14v24l-3-2-2 2-2-2-2 2-2-2-3 2z" /><path d="M12 10h8M12 14h8M12 18h5" /></>,
  retail: <><path d="M7 11h18l-1.5 15h-15z" /><path d="M12 11V9a4 4 0 0 1 8 0v2" /></>,
  giving: <path d="M16 25s-9-5.5-9-12a4.6 4.6 0 0 1 9-1.6A4.6 4.6 0 0 1 25 13c0 6.5-9 12-9 12z" />,
  stay: <><path d="M5 15L16 6l11 9" /><path d="M8 13v13h16V13" /><path d="M13 26v-6h6v6" /></>,
};

/**
 * App icon: a filled tile in the app's accent with a white line glyph (Google/Microsoft-style family).
 * Unknown glyph names fall back to the Nest mark.
 */
export function AppIconTile({ glyph, color, size = 48 }: { glyph: string; color: string; size?: number }) {
  const g = APP_GLYPHS[glyph];
  const id = `ixg-${glyph}`;
  return (
    <Box component="svg" width={size} height={size} viewBox="0 0 32 32" aria-hidden sx={{ display: 'block', flexShrink: 0 }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={color} stopOpacity={0.88} />
          <stop offset="1" stopColor={color} />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8.5" fill={`url(#${id})`} />
      {g ? (
        <g fill="none" stroke="#FFFFFF" strokeWidth={size <= 24 ? 2.2 : 1.9} strokeLinecap="round" strokeLinejoin="round">{g}</g>
      ) : (
        <path d={MARK_PATH} transform="scale(0.6667)" fill="none" stroke="#FFFFFF" strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
      )}
    </Box>
  );
}
